import React from 'react';
import { Content } from 'src/common/interfaces/content.interface';
import { useCurrentOrganization } from 'src/store/organization';
import { apiCreateContent, apiGetEmbedDetailsFromUrl, apiUploadContent } from 'src/utils/journeyApi';
import { runWithRetries, useEditorStore } from '../../editor-store';
import { EmbedCheckResponse, LinkDisplayOption, LinkResponse } from '../../links/types';
import {
  AspectRatio,
  AttachmentBlockContent,
  Block,
  BlockContent,
  LinkBlockContent,
  LogoBlockContent,
  BlockLayoutExtraParams,
  PdfBlockContent,
  PdfMetadata,
  VideoBlockContent,
  MsOfficeBlockContent,
} from '../../types';
import { getImageMetrics } from '../get-image-metrics';
import { getFileMetadata } from 'src/utils/pdf';
import { getVideoFileMetadata } from 'src/utils/video';
import { getAttachmentSubtitleFromBlockContent } from '../block-types/attachment';
import { FileAsset } from 'src/common/interfaces/file-asset.interface';
import { ImageAsset } from 'src/common/interfaces/image-asset.interface';
import { fileToBlockContentType } from '../file-to-block-content-type';
import { useImageBlockOperations } from '../block/use-image-block-operations';
import { urlContentTypeToBlockType } from '../url-content-type-to-block-type';
import { fetchUrlAsBlob } from 'src/utils/file';
import { useGetActiveLogoStack } from '../../logo/get-active-logo-stack.hook';
import { uploadFileToS3 } from 'src/common/helpers/file-upload';
import { useLogoStore } from '../../logo/store';

type Awaited<T> = T extends Promise<infer U> ? U : T;

export const useBlockActions = (block: Block) => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);

  const logoBlock = useGetActiveLogoStack(block);
  const updateLogoBlock = useLogoStore((state) => state.updateLogoBlock);

  const { onNewImageUrl, onNewImageFile } = useImageBlockOperations(block);

  const onLogoBlockUpdated = (payload: Partial<LogoBlockContent>) => {
    // console.info(logoBlock, ' < logoBlock');
    updateLogoBlock(block.id, payload);
    dispatchUserEditorAction(
      {
        type: 'update-block-content',
        id: block.id,
        content: {
          type: 'logo',
          selectedOrgLogo: logoBlock.selectedOrgLogo,
          selectedRecipientLogo: logoBlock.selectedRecipientLogo,
          ...payload,
        },
      },
      {
        syncable: true,
        undoable: false,
      }
    );
  };

  const onNewLink = (linkResponse: LinkResponse, displayOption: LinkDisplayOption) => {
    const content = {
      type: 'link',
      link: linkResponse,
      displayOption,
    } as LinkBlockContent;

    dispatchUserEditorAction(
      {
        type: 'replace-block',
        id: block.id,
        content: content,
        layoutExtraParams: {
          aspectRatio: {
            width: 1.77,
            height: 1,
          },
        },
      },
      {
        syncable: true,
        undoable: true,
      }
    );

    apiCreateContent(currentOrganization.id, {
      link_url: linkResponse.url,
    }).then((response: Content) => {
      dispatchUserEditorAction(
        {
          type: 'set-block-content-uuid',
          id: block.id,
          contentUUID: response.uuid,
        },
        {
          syncable: true,
          undoable: false,
        }
      );
    });
  };

  const createContentFromExternalFileUrl = async (url: string) => {
    const params = {
      external_file_url: url,
    };
    return apiCreateContent(currentOrganization.id, params);
  };

  const onDeleteBlock = () => {
    dispatchUserEditorAction({
      type: 'delete-block',
      id: block.id,
    });
  };

  const onBlockParamsChange = (
    blockId: string,
    params: any = {},
    config: { undoable: boolean; syncable: boolean } = { undoable: true, syncable: true }
  ) => {
    dispatchUserEditorAction(
      {
        type: 'update-block-content',
        id: block.id,
        content: params,
      },
      config
    );
  };

  const onNewVideoContentFromLibrary = (content: Content) => {
    const { video_asset } = content;
    if (!video_asset) {
      return;
    }

    const layoutExtraParams: BlockLayoutExtraParams = {};
    if (video_asset.mux_asset.aspect_ratio) {
      const [width, height] = video_asset.mux_asset.aspect_ratio.split(':');
      layoutExtraParams.aspectRatio = {
        width: parseInt(width, 10),
        height: parseInt(height, 10),
      };
    }

    dispatchUserEditorAction(
      {
        type: 'replace-block',
        content: {
          ...content,
          type: 'video',
          contentUUID: content.uuid,
          muxPlaybackId: video_asset.mux_asset.public_playback_id,
          metadata: {
            aspectRatio: layoutExtraParams.aspectRatio || undefined,
            name: video_asset.name,
            size: 0,
            duration: video_asset.duration || video_asset.mux_asset.duration || 0,
          },
          hasTranscript: video_asset.has_transcript,
        },
        id: block.id,
        layoutExtraParams,
      },
      { syncable: true, undoable: true }
    );
  };

  const onNewVideoFromExternalUrl = (
    url: string,
    metadata: any,
    importParams?: {
      source: string;
      url: string;
      data?: any;
    }
  ) => {
    const { name, duration } = metadata;
    const layoutExtraParams: BlockLayoutExtraParams = {};
    let aspectRatio: AspectRatio = { width: 16, height: 9 };
    layoutExtraParams.aspectRatio = aspectRatio;

    dispatchUserEditorAction(
      {
        type: 'replace-block',
        content: {
          type: 'video',
          externalUrl: url,
          metadata: {
            aspectRatio,
            name: name,
            size: 0,
            duration: duration,
          },
          importParams,
        },
        id: block.id,
        layoutExtraParams,
      },
      { syncable: true, undoable: true }
    );
  };

  const onNewPdfContentFromLibrary = async (content: Content, onSuccess: () => void) => {
    const { file_asset } = content;

    if (!file_asset) {
      return;
    }

    const pdfHasThumbnail = content.thumbnail ? !!content.thumbnail.url : false;
    const pdfMetadata = await getFileMetadata(content.file_asset!.url, pdfHasThumbnail);

    const metadata: PdfMetadata = {
      name: file_asset.name,
      width: pdfMetadata?.width || file_asset.metadata?.width || 0,
      height: pdfMetadata?.height || file_asset.metadata?.height || 0,
      numPages: pdfMetadata?.num_pages || file_asset.metadata?.num_pages || 0,
      size: 0,
    };

    const layoutExtraParams: BlockLayoutExtraParams = {};

    if (file_asset.metadata?.aspect_ratio) {
      const [width, height] = file_asset.metadata.aspect_ratio.split(':');
      layoutExtraParams.aspectRatio = {
        width: parseInt(width, 10),
        height: parseInt(height, 10),
      };
    }

    dispatchUserEditorAction(
      {
        type: 'replace-block',
        layoutExtraParams,
        id: block.id,
        content: {
          metadata,
          type: 'pdf',
          jnyContent: content,
          contentUUID: content.uuid,
          allowDownload: true,
          thumbnailUrl: content.thumbnail?.url || null,
        },
      },
      { syncable: true, undoable: true }
    );
    onSuccess();
  };

  const onNewAttachmentContentFromLibrary = (
    asset: FileAsset | ImageAsset,
    contentUuid: string,
    onSuccess?: () => void
  ) => {
    if (!asset) {
      return;
    }

    const attachmentBlockContent: AttachmentBlockContent = {
      type: 'attachment',
      contentType: asset.content_type,
      contentUUID: contentUuid,
      url: asset.url,
      title: asset.name,
      subtitle: '',
    };

    attachmentBlockContent.subtitle = getAttachmentSubtitleFromBlockContent(attachmentBlockContent);

    dispatchUserEditorAction(
      {
        type: 'replace-block',
        id: block.id,
        content: attachmentBlockContent,
        layoutExtraParams: {},
      },
      {
        syncable: true,
        undoable: true,
      }
    );
    if (onSuccess) {
      onSuccess();
    }
  };

  const onNewMsOfficeContentFromLibrary = (asset: FileAsset, contentUUID: string, onSuccess?: () => void) => {
    if (!asset) {
      return;
    }

    const msOfficeBlockContent: MsOfficeBlockContent = {
      type: 'ms-office',
      contentType: asset.content_type,
      url: asset.url,
      name: asset.name,
      contentUUID: contentUUID,
    };

    dispatchUserEditorAction(
      {
        type: 'replace-block',
        id: block.id,
        content: msOfficeBlockContent,
        layoutExtraParams: {},
      },
      {
        syncable: true,
        undoable: true,
      }
    );
    if (onSuccess) {
      onSuccess();
    }
  };

  const onNewUrl = async (url: string, title = '') => {
    const response: EmbedCheckResponse = await apiGetEmbedDetailsFromUrl(url);
    const { content_type } = response;

    const blockContentType = urlContentTypeToBlockType(url, content_type);

    if (blockContentType === 'link') {
      const linkResponse: LinkResponse = {
        url: response.url,
        transformed_url: response.transformed_url,
        title: title || response.oembed.title,
        thumbnail: {
          url: response.oembed?.thumbnail_url || null,
          width: response.oembed?.thumbnail_width || null,
          height: response.oembed?.thumbnail_height || null,
        },
        destination: response.destination,
        iframe_safe: response.iframe_safe,
        third_party_iframe_safe: response.third_party_iframe_safe,
      };

      onNewLink(linkResponse, response.iframe_safe ? 'inline' : 'new-tab');
    } else if (blockContentType === 'video') {
      onNewVideoFromExternalUrl(response.url, response.meta_data);
    } else if (blockContentType === 'image' || blockContentType === 'pdf' || blockContentType === 'ms-office') {
      // await createContentFromExternalFileUrl(response.url);
      // onNewImageUrl({ url });

      // onNewPdfContentFromLibrary(content);
      // we need to upload the file so we don't run into CORS issue here
      // const content = await createContentFromExternalFileUrl(response.url);

      const { url, name, content_type } = await apiUploadContent(response.url);

      const fileBlob = await fetchUrlAsBlob(url);
      const file = new File([fileBlob], name, { type: content_type });

      if (blockContentType === 'image') {
        onNewImageFile(file);
        return;
      } else {
        let initialContent: BlockContent | undefined;
        let layoutExtraParams: BlockLayoutExtraParams = {};
        if (blockContentType === 'pdf') {
          initialContent = await getInitialPdfBlockContent(file);
          if (initialContent) {
            const { metadata } = initialContent;
            const aspectRatio = metadata && metadata.width && metadata.height ? metadata : undefined;
            layoutExtraParams = { aspectRatio };
          }
        } else if (blockContentType === 'ms-office') {
          initialContent = await getInitialMsOfficeBlockContent(file);
        }

        if (!initialContent) {
          onBlockParamsChange(block.id, { fileStatus: 'error' });
          return;
        }
        dispatchUserEditorAction(
          {
            type: 'replace-block',
            id: block.id,
            content: initialContent,
            layoutExtraParams,
          },
          {
            syncable: true,
            undoable: true,
          }
        );
      }

      // let layoutExtraParams: BlockLayoutExtraParams = {};

      // const metadata = await getFileMetadata(url, false);
      // if (metadata) {
      //   const aspectRatio = metadata && metadata.width && metadata.height ? metadata : { width: 16, height: 9 };
      //   layoutExtraParams = { aspectRatio };
      // }
      // console.log("metadata is", metadata);

      // // // bg-neue-gray-darker
      // let dominantColor = '#d2cacb';

      // if (metadata && metadata.thumbnail) {
      //   const { color } = await getImageMetrics(metadata.thumbnail!);
      //   dominantColor = color;
      // }

      // dispatchUserEditorAction(
      //   {
      //     type: 'replace-block',
      //     id: block.id,
      //     content: {
      //       type: 'pdf',
      //       jnyContentUUID: content.uuid,
      //       jnyContent: content,
      //       metadata: {
      //         name: name,
      //         size: 0,
      //         width: metadata?.width || 0,
      //         height: metadata?.height || 0,
      //         numPages: metadata?.num_pages || 0,
      //         dominantColor: dominantColor,
      //       },
      //       thumbnailUrl: content.thumbnail?.url || metadata?.thumbnail || null,
      //     },
      //     layoutExtraParams,
      //   },
      //   {
      //     syncable: true,
      //     undoable: true,
      //   }
      // );

      // onBlockParamsChange(block.id, { fileStatus: undefined });
      // onNewPdfUrl(url);
    }
  };

  const onNewFile = async (file: File) => {
    // console.log('onNewFile', file);
    const blockContentType: BlockContent['type'] = fileToBlockContentType(file);

    if (blockContentType === 'image') {
      onNewImageFile(file);
      return;
    }

    let initialContent: BlockContent | undefined;
    let layoutExtraParams: BlockLayoutExtraParams = {};
    if (blockContentType === 'pdf') {
      initialContent = await getInitialPdfBlockContent(file);
      if (initialContent) {
        const { metadata } = initialContent;
        const aspectRatio = metadata && metadata.width && metadata.height ? metadata : undefined;
        layoutExtraParams = { aspectRatio };
      }
    } else if (blockContentType === 'attachment') {
      initialContent = await getInitialAttachmentBlockContent(file);
    } else if (blockContentType === 'video') {
      initialContent = await getInitialVideoBlockContent(file);
      if (initialContent) {
        layoutExtraParams = { aspectRatio: initialContent.metadata?.aspectRatio };
      }
    } else if (blockContentType === 'ms-office') {
      initialContent = await getInitialMsOfficeBlockContent(file);
    }
    // console.log('initialContent', initialContent);
    if (!initialContent) {
      onBlockParamsChange(block.id, { fileStatus: 'error' });
      return;
    }
    dispatchUserEditorAction(
      {
        type: 'replace-block',
        id: block.id,
        content: initialContent,
        layoutExtraParams,
      },
      {
        syncable: true,
        undoable: true,
      }
    );
  };

  const onUploadNewFile = (file: File, folder = 'logos') => {
    return runWithRetries(() => uploadFileToS3(file, folder));
  };

  const getInitialPdfBlockContent = async (file: File): Promise<PdfBlockContent | undefined> => {
    let metadata = await getFileMetadata(file, false);
    if (!metadata) {
      return;
    }
    return {
      type: 'pdf',
      file: file,
      fileUploadStatus: 'none',
      fileUploadProgress: 0,
      metadata: {
        width: metadata.width,
        height: metadata.height,
        size: file.size,
        name: file.name,
        numPages: metadata.num_pages,
      },
      thumbnailUrl: null,
    };
  };

  const getInitialAttachmentBlockContent = async (file: File): Promise<AttachmentBlockContent | undefined> => {
    return {
      type: 'attachment',
      file: file,
      contentType: file.type,
      metadata: {
        size: file.size,
        name: file.name,
      },
      title: '',
      subtitle: '',
    };
  };

  const getInitialMsOfficeBlockContent = async (file: File): Promise<MsOfficeBlockContent | undefined> => {
    return {
      type: 'ms-office',
      name: file.name,
      file: file,
      contentType: file.type,
    };
  };

  const getInitialVideoBlockContent = async (file: File): Promise<VideoBlockContent> => {
    let videoFileMetadata: Awaited<ReturnType<typeof getVideoFileMetadata>> | undefined;
    try {
      videoFileMetadata = await getVideoFileMetadata(file);
    } catch (e) {
      console.error(e);
    }
    const metadata = {
      size: file.size,
      name: file.name,
      ...(videoFileMetadata && {
        aspectRatio: { width: videoFileMetadata.width, height: videoFileMetadata.height },
        duration: videoFileMetadata.duration,
      }),
    };
    return {
      type: 'video',
      file: file,
      metadata,
    };
  };

  return {
    onLogoBlockUpdated,
    onNewLink,
    onDeleteBlock,
    onBlockParamsChange,
    onNewUrl,
    onUploadNewFile,
    onNewFile,
    onNewPdfContentFromLibrary,
    onNewAttachmentContentFromLibrary,
    onNewMsOfficeContentFromLibrary,
    onNewVideoContentFromLibrary,
    onNewVideoFromExternalUrl,
  };
};
